import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AsyncSubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import spacetime from 'spacetime';
import { StudentCourseFeedbackModel } from 'src/lib/services/api/students/classes/student-course-feedback.model';
import {
	StudentCourseActivitiesFolderModel,
	StudentCourseActivitiesModel,
	StudentCourseActivityFolderModel,
} from 'src/lib/services/api/students/classes/student-course.model';
import { ActiveStudentService } from 'src/lib/services/startup/active-student/active-student.service';
import { StudentCourseFeedbackStoreService } from 'src/lib/services/stores/students/course-feedback/student-course-feedback-store.service';
import { StudentCourseStoreService } from 'src/lib/services/stores/students/course/student-course-store.service';
import { InitializationService } from 'src/lib/services/utility/initialization/initialization.service';
import { hasValue } from 'src/lib/utilities/compare';
import { tryParseInt } from 'src/lib/utilities/convert';
import {
	getCourseActivityType,
	getRegistrationActivityAccessState,
	RegistrationActivityAccessState,
} from 'src/lib/utilities/site/course';
import { firstBy } from 'thenby';

export class AbstractCourseAssignmentsComponent {
	protected _unsubscribe$ = new AsyncSubject<null>();

	public RegistrationActivityAccessState = RegistrationActivityAccessState;

	public studentClassActivities: StudentCourseActivitiesModel;
	public classFeedback: StudentCourseFeedbackModel;
	public studentUid: number;
	public registrationId: number;
	public classComplete: boolean = false;
	public loaded: boolean = false;

	private activityAccessStateMap = new Map<
		string,
		RegistrationActivityAccessState
	>();
	private folderLayoutMap = new Map<
		StudentCourseActivitiesFolderModel,
		{
			assignmentActivities: Map<string, string[]>;
			activityAssignment: Map<string, string>;
		}
	>();
	public bookmarkId: string;

	public hasError: boolean = false;

	constructor(
		protected route: ActivatedRoute,
		protected activeStudent: ActiveStudentService,
		protected studentsClassesStore: StudentCourseStoreService,
		protected studentCourseFeedbackStoreService: StudentCourseFeedbackStoreService,
		protected initService: InitializationService,
		protected cdr: ChangeDetectorRef,
	) {}

	baseOnInit(): void {
		combineLatest([this.activeStudent.getActiveStudent$(), this.route.params])
			.pipe(
				map(([studentId, p]) => {
					const regId = tryParseInt(p.registrationId);

					return [studentId, regId];
				}),
				switchMap(([studentId, registrationId]) =>
					combineLatest([
						this.studentsClassesStore.classActivities$(
							studentId,
							registrationId,
						),
						this.studentsClassesStore.class$(studentId, registrationId),
						this.studentCourseFeedbackStoreService.registrationFeedback$(
							studentId,
							registrationId,
						),
						of(studentId),
						of(registrationId),
					]),
				),
			)
			.subscribe(
				([
					studentClassActivities,
					courseResult,
					feedback,
					studentUid,
					registrationId,
				]) => {
					this.loaded = true;
					if (courseResult.errored || studentClassActivities == null) {
						this.hasError = true;
						this.cdr.detectChanges();
						return; // The registration is broken, abort
					}

					this.studentUid = studentUid;
					this.registrationId = registrationId;
					this.classFeedback = feedback;
					this.studentClassActivities = studentClassActivities;
					this.classComplete = hasValue(courseResult.value.completed_date);

					this.activityAccessStateMap.clear();
					this.folderLayoutMap.clear();

					//filter out hidden classes
					this.studentClassActivities.folders =
						this.studentClassActivities.folders?.map((f) => {
							f.activities = f.activities.filter((x) => !x.hidden);
							return f;
						});

					this.studentClassActivities.folders?.forEach((f) => {
						const assignments = this.getFolderAssignments(f);
						const assignmentActivitiesMap = new Map<string, string[]>();
						const activityAssignmentMap = new Map<string, string>();

						let ai = 0;
						// eslint-disable-next-line @typescript-eslint/prefer-for-of
						for (let i = 0; i < f.activities.length; i++) {
							const assignmentId = assignments[ai]?.id;
							const activityId = f.activities[i].id;

							if (assignmentId === null) {
								break;
							}

							if (!assignmentActivitiesMap.has(assignmentId)) {
								assignmentActivitiesMap.set(assignmentId, []);
							}

							assignmentActivitiesMap.get(assignmentId).push(activityId);
							activityAssignmentMap.set(activityId, assignmentId);
							if (assignmentId === activityId) {
								ai++;
							}
						}

						if (this.classComplete) {
							f.metadata.open = true;
						}

						this.folderLayoutMap.set(f, {
							assignmentActivities: assignmentActivitiesMap,
							activityAssignment: activityAssignmentMap,
						});
					});

					this.setBookmark();

					this.cdr.detectChanges();
				},
			);
	}

	public getAssignmentType = getCourseActivityType;

	public getAccessState = (activity: StudentCourseActivityFolderModel) => {
		if (!this.initService.hasAnyRole('student')) {
			return RegistrationActivityAccessState.locked;
		}

		if (!this.activityAccessStateMap.has(activity.id)) {
			this.activityAccessStateMap.set(
				activity.id,
				getRegistrationActivityAccessState(activity),
			);
		}

		return this.activityAccessStateMap.get(activity.id);
	};

	public getActivityFeedback = (activity: StudentCourseActivityFolderModel) => {
		return (this.classFeedback?.assignment_feedback ?? [])
			.filter((f) => f.assignment_id === activity.id)
			.sort(firstBy((f) => f.feedback_date, -1))[0];
	};

	public getFolderAssignments = (
		folder: StudentCourseActivitiesFolderModel,
	) => {
		return folder.activities.filter(
			(a) => hasValue(a.points_possible) && !a.hidden,
		);
	};

	public getSubmittedAssignments = (
		assignments: StudentCourseActivityFolderModel[],
	) => {
		return assignments?.filter((a) => a.submitted_date != null);
	};

	public getFolderState = (folder: StudentCourseActivitiesFolderModel) => {
		const assignments = this.getFolderAssignments(folder);
		const oneWeekAway = spacetime.now().add(1, 'week').endOf('date');

		if (assignments.filter((a) => a.uncompleted_assignment).length === 0) {
			return 'complete';
		} else if (assignments.filter((a) => a.past_due).length > 0) {
			return 'past';
		} else if (
			assignments.filter((a) => spacetime(a.due_date).isBefore(oneWeekAway))
				.length > 0
		) {
			return 'current';
		} else {
			return 'future';
		}
	};

	protected setBookmark = (): void => {
		const allAssignments = this.studentClassActivities.folders
			.map((f) =>
				this.getFolderAssignments(f).map((a) => {
					return {
						folder: f,
						assignment: a,
					};
				}),
			)
			.flat();

		const completedAssignments = allAssignments.filter(
			(x) => !x.assignment.uncompleted_assignment,
		);

		const lastCompletedAssignment =
			completedAssignments[completedAssignments.length - 1]?.assignment;

		const lastCAIndex = allAssignments.findIndex(
			(x) => x.assignment === lastCompletedAssignment,
		);

		const nextAssignment = allAssignments[lastCAIndex + 1];

		this.bookmarkId = null;
		if (nextAssignment) {
			const assignmentActivities = this.folderLayoutMap
				.get(nextAssignment.folder)
				.assignmentActivities.get(nextAssignment.assignment.id);

			this.bookmarkId = assignmentActivities[0];
		}

		if (this.classComplete) {
			this.bookmarkId = null;
		}
	};

	baseOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
